import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand.jsx";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Pill, EmptyState } from "@/components/ui-bits";
import { formatINR, fmtDate, statusTone, today } from "@/lib/format";
import { BACKEND_URL } from "@/lib/config";
import { Plus, FileText, Download, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { value: "tax_invoice", label: "Tax Invoice" },
  { value: "proforma", label: "Proforma" },
  { value: "receipt", label: "Receipt" },
  { value: "credit_note", label: "Credit Note" },
];

const STATUSES = ["Draft", "Sent", "Partial", "Paid", "Overdue", "Cancelled"];

const newItem = () => ({ description: "", quantity: 1, rate: 0, gst_rate: 18 });

const empty = () => ({
  doc_type: "tax_invoice",
  brand_id: "",
  client_id: "",
  project_id: "",
  issue_date: today(),
  due_date: today(),
  items: [newItem()],
  currency: "INR",
  notes: "",
  template_id: "",
  status: "Draft",
});

export default function Invoices() {
  const { brands, brandQuery, activeBrandId } = useBrand();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  const load = useCallback(async () => {
    const [a, b, c, t] = await Promise.all([
      api.get("/invoices", { params: brandQuery }),
      api.get("/clients"),
      api.get("/projects"),
      api.get("/templates"),
    ]);
    setItems(a.data); setClients(b.data); setProjects(c.data); setTemplates(t.data);
  }, [brandQuery]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ ...empty(), brand_id: activeBrandId !== "all" ? activeBrandId : brands[0]?.id || "" });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        template_id: form.template_id || null,
        items: form.items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity || 0),
          rate: Number(i.rate || 0),
          gst_rate: Number(i.gst_rate || 0),
        })),
      };
      const r = await api.post("/invoices", payload);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setOpen(false);
      nav(`/invoices/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (i) => {
    if (!window.confirm(`Delete invoice ${i.invoice_number}?`)) return;
    await api.delete(`/invoices/${i.id}`); load();
  };

  const subtotal = form.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.rate || 0), 0);
  const gst = form.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.rate || 0) * Number(i.gst_rate || 0) / 100, 0);
  const total = subtotal + gst;

  return (
    <div data-testid="invoices-page">
      <PageHeader
        eyebrow="Billing"
        title="Invoices"
        description="Proformas, tax invoices, receipts and credit notes — branded per division."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild className="border-border bg-white">
              <a href={`${BACKEND_URL}/api/exports/invoices?fmt=xlsx`} target="_blank" rel="noreferrer">
                <Download className="w-4 h-4 mr-1.5" /> Export
              </a>
            </Button>
            <Button data-testid="new-invoice-btn" onClick={openCreate} className="bg-bronze text-black hover:bg-[#d4af68]">
              <Plus className="w-4 h-4 mr-1.5" /> New invoice
            </Button>
          </div>
        }
      />

      {items.length === 0 ? (
        <EmptyState title="No invoices yet" description="Create your first invoice — proforma or tax — and we'll generate a PDF." />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Number</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Type</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Client</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Issued</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Due</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground text-right">Balance</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Status</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => {
                const client = clients.find((c) => c.id === i.client_id);
                const brand = brands.find((b) => b.id === i.brand_id);
                return (
                  <TableRow key={i.id} className="border-border hover:bg-[#f8f6f2]" data-testid={`invoice-row-${i.id}`}>
                    <TableCell>
                      <Link to={`/invoices/${i.id}`} className="font-mono text-bronze hover:underline">{i.invoice_number}</Link>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{brand?.name}</div>
                    </TableCell>
                    <TableCell className="text-xs">{(i.doc_type || "").replace("_", " ")}</TableCell>
                    <TableCell className="text-sm">{client?.company || client?.name || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(i.issue_date)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(i.due_date)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatINR(i.total)}</TableCell>
                    <TableCell className={`text-right font-mono text-sm ${i.balance_due > 0 ? "text-bronze" : "text-muted-foreground"}`}>{formatINR(i.balance_due)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-medium border ${statusTone(i.status)}`}>
                        {i.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a href={`${BACKEND_URL}/api/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-bronze" data-testid={`invoice-pdf-${i.id}`}>
                          <FileText className="w-4 h-4" />
                        </a>
                        <button onClick={() => del(i)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invoice-dialog" className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">New invoice</DialogTitle></DialogHeader>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label>Type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Brand</Label>
              <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Brand" /></SelectTrigger>
                <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project (optional)</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Issue date</Label><Input type="date" value={form.issue_date} onChange={(e)=>setForm({...form, issue_date:e.target.value})} className="bg-white" /></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e)=>setForm({...form, due_date:e.target.value})} className="bg-white" /></div>
            <div>
              <Label>Template (optional)</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Built-in" /></SelectTrigger>
                <SelectContent>{templates.filter((t)=>!form.brand_id || t.brand_id===form.brand_id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={(e)=>setForm({...form, currency:e.target.value})} className="bg-white" /></div>
          </div>

          <div className="mt-2 border border-border rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground bg-[#fafaf9] border-b border-border">
              <div className="col-span-6">Description</div>
              <div className="col-span-1 text-right">Qty</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-2 text-right">GST %</div>
              <div className="col-span-1" />
            </div>
            {form.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-0 items-center" data-testid={`invoice-item-row-${idx}`}>
                <Input className="col-span-6 bg-white" value={it.description} onChange={(e)=>{
                  const items = [...form.items]; items[idx] = { ...it, description: e.target.value }; setForm({ ...form, items });
                }} />
                <Input className="col-span-1 bg-white text-right" type="number" value={it.quantity} onChange={(e)=>{
                  const items = [...form.items]; items[idx] = { ...it, quantity: e.target.value }; setForm({ ...form, items });
                }} />
                <Input className="col-span-2 bg-white text-right font-mono" type="number" value={it.rate} onChange={(e)=>{
                  const items = [...form.items]; items[idx] = { ...it, rate: e.target.value }; setForm({ ...form, items });
                }} />
                <Input className="col-span-2 bg-white text-right" type="number" value={it.gst_rate} onChange={(e)=>{
                  const items = [...form.items]; items[idx] = { ...it, gst_rate: e.target.value }; setForm({ ...form, items });
                }} />
                <button className="col-span-1 text-muted-foreground hover:text-rose-400" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}>
                  <X className="w-4 h-4 mx-auto" />
                </button>
              </div>
            ))}
            <div className="px-3 py-2 bg-[#fafaf9]">
              <button onClick={() => setForm({ ...form, items: [...form.items, newItem()] })} className="text-xs text-bronze hover:underline" data-testid="add-line-item">
                + Add line
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="bg-white" /></div>
            <div className="rounded-lg border border-border p-3 bg-[#fafaf9] text-sm font-mono">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground mt-1"><span>GST</span><span>{formatINR(gst)}</span></div>
              <div className="flex justify-between text-bronze font-semibold mt-2 pt-2 border-t border-border"><span>Total</span><span>{formatINR(total)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-invoice-btn" onClick={submit} className="bg-bronze text-black hover:bg-[#d4af68]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
